<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import EmptyState from '@/Components/UI/EmptyState.vue'

const { t } = useI18n()
const { formatDate } = useCurrency()

const props = defineProps({
  range:       { type: Object, required: true },
  rentals:     { type: Array,  required: true },
  maintenance: { type: Array,  required: true },
  vehicles:    { type: Array,  required: true },
})

const startDate = computed(() => new Date(props.range.from))

const TOTAL_DAYS = 7

const dayCells = computed(() => {
  const out = []
  for (let i = 0; i < TOTAL_DAYS; i++) {
    const d = new Date(startDate.value)
    d.setDate(d.getDate() + i)
    out.push({
      date:   d,
      label:  formatDate(d, { weekday: 'short' }),
      day:    d.getDate(),
      isToday: isSameDay(d, new Date()),
    })
  }
  return out
})

const vehicleLookup = computed(() => Object.fromEntries(props.vehicles.map(v => [v.id, v])))

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function dayIndex(iso) {
  const d = new Date(iso)
  return Math.floor((d.setHours(0, 0, 0, 0) - new Date(startDate.value).setHours(0, 0, 0, 0)) / 86_400_000)
}

function clip(start, end) {
  const s = Math.max(0, Math.min(TOTAL_DAYS, dayIndex(start)))
  const e = Math.max(0, Math.min(TOTAL_DAYS, dayIndex(end) + 1))
  return { dayStart: s, daySpan: Math.max(1, e - s) }
}

function leftPct(dayStart) { return (dayStart / TOTAL_DAYS) * 100 }
function widthPct(daySpan) { return (daySpan / TOTAL_DAYS) * 100 }

function packLanes(bars) {
  const sorted = [...bars].sort((a, b) => a.dayStart - b.dayStart)
  const laneEnds = [] // laneEnds[i] = exclusive end day of last bar in lane i
  for (const bar of sorted) {
    let lane = laneEnds.findIndex(end => end <= bar.dayStart)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(0)
    }
    bar.slot = lane
    laneEnds[lane] = bar.dayStart + bar.daySpan
  }
  return Math.max(1, laneEnds.length)
}

const rows = computed(() => {
  const byVehicle = new Map()
  const ensureRow = (vid) => {
    if (!byVehicle.has(vid)) byVehicle.set(vid, { vehicle: vehicleLookup.value[vid], bars: [] })
    return byVehicle.get(vid)
  }
  for (const r of props.rentals) {
    const { dayStart, daySpan } = clip(r.bar_start_at, r.bar_end_at)
    if (daySpan < 1) continue
    ensureRow(r.vehicle_id).bars.push({
      kind:   'rental',
      key:    `r-${r.id}`,
      dayStart, daySpan,
      color:  r.color_token,
      href:   r.href,
      label:  r.customer_name,
      title:  `${r.customer_name} · ${formatDate(r.bar_start_at, { month: 'short', day: 'numeric' })} – ${formatDate(r.bar_end_at, { month: 'short', day: 'numeric' })}`,
    })
  }
  for (const m of props.maintenance) {
    const end = m.ended_at || m.planned_end || props.range.to
    const { dayStart, daySpan } = clip(m.started_at, end)
    if (daySpan < 1) continue
    ensureRow(m.vehicle_id).bars.push({
      kind:   'maintenance',
      key:    `m-${m.id}`,
      dayStart, daySpan,
      color:  '--warn',
      href:   m.href,
      label:  t('calendar.maintenance'),
      title:  t('calendar.maintenance'),
    })
  }
  const result = Array.from(byVehicle.values()).filter(r => r.vehicle)
  for (const row of result) {
    row.lanes = packLanes(row.bars)
  }
  return result
})

const isEmpty = computed(() => rows.value.length === 0)
</script>

<template>
  <EmptyState v-if="isEmpty" type="success" :title="t('calendar.empty.week')" />

  <div
    v-else
    class="overflow-x-auto rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]"
  >
    <div
      class="min-w-[640px] grid"
      style="grid-template-columns: 12rem repeat(7, minmax(0, 1fr));"
    >
      <!-- Header row -->
      <div class="px-3 py-2 sticky left-0 bg-[color:var(--surface)] z-10"></div>
      <div
        v-for="cell in dayCells"
        :key="`${cell.label}-${cell.day}`"
        class="px-2 py-2 text-xs text-[color:var(--inkSoft)] border-l border-[color:var(--line)]"
        :class="cell.isToday ? 'bg-[color:var(--accentSoft)]' : ''"
      >
        <div class="uppercase tracking-wider text-[10px] text-[color:var(--muted)]">{{ cell.label }}</div>
        <div class="text-sm fw-value">{{ cell.day }}</div>
      </div>

      <!-- Vehicle rows -->
      <template v-for="row in rows" :key="row.vehicle.id">
        <div class="px-3 py-3 text-sm text-[color:var(--ink)] sticky left-0 bg-[color:var(--surface)] z-10 border-t border-[color:var(--line)] truncate">
          {{ row.vehicle.label }}<span v-if="row.vehicle.registration" class="text-[color:var(--muted)]"> · {{ row.vehicle.registration }}</span>
        </div>
        <div class="col-span-7 relative border-t border-[color:var(--line)] py-2" :style="{ minHeight: `calc(0.5rem + ${row.lanes} * 2rem + 0.5rem)` }">
          <Link
            v-for="bar in row.bars"
            :key="bar.key"
            :href="bar.href"
            :title="bar.title"
            class="absolute h-7 rounded-[var(--radius-button)] px-2 text-xs flex items-center truncate text-[color:var(--bg)]"
            :style="{
              left:   `calc(${leftPct(bar.dayStart)}% + 2px)`,
              width:  `calc(${widthPct(bar.daySpan)}% - 4px)`,
              top:    `calc(0.5rem + ${bar.slot} * 2rem)`,
              background: bar.kind === 'maintenance'
                ? `repeating-linear-gradient(45deg, var(${bar.color}), var(${bar.color}) 4px, color-mix(in oklch, var(${bar.color}) 70%, black) 4px, color-mix(in oklch, var(${bar.color}) 70%, black) 8px)`
                : `var(${bar.color})`,
            }"
          >
            <span class="truncate">{{ bar.label }}</span>
          </Link>
        </div>
      </template>
    </div>
  </div>
</template>
